'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { useReducedMotion, useCollectionClock } from '../use-animation-clock';
import { useMostlyInView, ShowcaseIcon } from './scene-primitives';
import { collectionSteps, dl } from './trend-research';
import {
  xl,
  bl,
  useCollectionHeight,
  CollectionPrompt,
  CollectionStepRail,
} from './collection-navigation';
import { SegmentedControl } from './playback-controls';
import { referenceProducts, he } from './reference-products';
import { referenceImages, Me } from './reference-brands';
export function CollectionAnimation() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let e = useReducedMotion(),
    [t, n] = useMostlyInView(0.55),
    [r, i] = React.useState(0),
    [a, o] = React.useState(0),
    [s, c] = React.useState(!0),
    [l, u] = React.useState(null),
    d = collectionSteps[r] ?? collectionSteps[0],
    f = d?.ms ?? 5e3,
    p = s && !e,
    m = f + (p && r < xl ? bl : 0),
    h = useCollectionClock({
      id: `${r}-${a}`,
      duration: m,
      running: n === !0,
      reduced: e,
    }),
    g = (e, t) => {
      (e === r
        ? o((e) => e + 1)
        : (u({
            index: r,
            t: Math.min(h, f),
          }),
          i(e),
          o((e) => e + 1)),
        t || c(!1));
    };
  React.useEffect(() => {
    !p ||
      r >= xl ||
      h < m ||
      (u({
        index: r,
        t: f,
      }),
      i(r + 1),
      o((e) => e + 1));
  }, [p, r, h, m, f]);
  let [ee, te] = useCollectionHeight(r),
    ne = d?.stage ?? 'plan',
    b = collectionSteps
      .map((e, t) => ({
        workflow: e,
        index: t,
      }))
      .filter((e) => e.workflow.stage === ne),
    re = h >= f,
    ie = re && (!p || r === xl);
  return (
    <div>
      <div ref={t} className={'sf rounded-[20px] p-2'}>
        <div
          className={
            'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-2 pt-1 pb-2'
          }
        >
          <CollectionPrompt />
          <SegmentedControl
            label={'Collection stage'}
            size={'sm'}
            value={ne}
            options={dl}
            onChange={(e) => {
              let t = collectionSteps.findIndex((t) => t.stage === e);
              t >= 0 && e !== ne && g(t, !1);
            }}
          />
        </div>
        <div
          className={
            'grid gap-2 md:grid-cols-[272px_minmax(0,1fr)] lg:grid-cols-[296px_minmax(0,1fr)]'
          }
        >
          <CollectionStepRail
            items={b}
            active={r}
            progress={h / m}
            finished={re}
            onSelect={(e) => g(e, !1)}
          />
          <div
            ref={ee}
            className={
              'cf-stage relative grid overflow-hidden rounded-[12px] bg-[var(--canvas)] shadow-[inset_0_0_0_1px_var(--line)] md:!h-[568px] md:grid-rows-[minmax(0,1fr)]'
            }
            style={
              te
                ? {
                    height: te,
                  }
                : void 0
            }
          >
            {translateShowcaseNode(
              showcaseCopy,
              collectionSteps.map((e, t) => {
                let n = t === r,
                  i = l && l.index === t ? l.t : 0,
                  Component_o = e.Artifact;
                return (
                  <div
                    key={e.key}
                    className={'panel min-w-0'}
                    data-active={n}
                    inert={!n}
                    aria-hidden={!n}
                  >
                    <Component_o
                      key={n ? `run-${a}` : 'idle'}
                      t={n ? Math.min(h, e.ms) : i}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <div
        className={'mt-3 flex min-h-10 items-center justify-between gap-4 px-1'}
      >
        <p className={'text-ink-4 text-[12px] leading-4 text-pretty'}>
          {translateShowcaseNode(
            showcaseCopy,
            'LISI\xC8RE is a fictional label. Research, gauges, gates and prices are from a real IMAI run; roles, options, stories and material assignment are illustrative.'
          )}
        </p>
        <button
          type={'button'}
          onClick={() => (p && r === xl ? g(0, !0) : g(r, s))}
          className={
            'btn btn-sm btn-soft press shrink-0 transition-[opacity,background-color] duration-200'
          }
          style={{
            opacity: +!!ie,
            pointerEvents: ie ? 'auto' : 'none',
          }}
          tabIndex={ie ? 0 : -1}
          aria-hidden={!ie}
        >
          <ShowcaseIcon name={'replay'} className={'size-3.5'} />
          {translateShowcaseNode(showcaseCopy, 'Replay')}
        </button>
      </div>
    </div>
  );
}
export const Il = [
  'topcoat',
  'crewneck',
  'trouser',
  'cardigan',
  'knit',
  'jacket',
];
export const Ll = {
  Taupe: '#A39382',
  Chalk: '#ECE8DF',
  Ink: '#23262C',
  Oxblood: '#5E1F23',
  Umber: '#6B4A33',
};
export const Rl = (e) => Math.round(e / 2);
export const zl = referenceProducts.flatMap((e, t) => {
  let n = Il[t],
    r = referenceImages.lisiere.pieces[t];
  return !n || !r
    ? []
    : [
        {
          id: n,
          title: e.style,
          name: e.name,
          category: e.category,
          role: e.role,
          colorway: e.colorway,
          hex: Ll[e.colorway] ?? '#8B7E6E',
          retail: e.price,
          wholesale: Rl(e.price),
          number: `LS-27${String(t + 1).padStart(2, '0')}`,
          asset: r,
        },
      ];
});
export const Bl = (e) => zl.find((t) => t.id === e);
export const Vl = (e, t = '$') => `${t}${e.toLocaleString('en-US')}`;
export const Hl = Me.lisiere;
export const Ul = [
  {
    key: 'online',
    label: 'Own e-commerce',
    short: 'Online',
    ids: ['topcoat', 'cardigan', 'trouser', 'crewneck', 'knit', 'jacket'],
    prices: 'retail',
    note: 'The full line at proposed retail',
    tag: (e, t) => (t ? 'Homepage' : null),
  },
  {
    key: 'flagship',
    label: 'NY flagship',
    short: 'Flagship',
    ids: ['jacket', 'topcoat', 'trouser', 'knit', 'cardigan'],
    prices: 'retail',
    note: 'Tailoring leads the floor',
    tag: (e, t) => (t ? 'Window' : null),
  },
  {
    key: 'wholesale',
    label: 'Wholesale',
    short: 'Wholesale',
    ids: ['topcoat', 'trouser', 'crewneck', 'cardigan'],
    prices: 'wholesale',
    note: 'Core styles at about 50% of retail',
    tag: (e) => e.role,
  },
];
export const Wl = {
  slides: 11,
  audience: 'NY specialty stores',
  delivery: 'Fall 2027, subject to sample approval',
  terms: [
    {
      label: 'Wholesale',
      value: 'About 50% of proposed retail',
    },
    {
      label: 'Delivery',
      value: 'Fall 2027, subject to sample approval',
    },
    {
      label: 'Minimums',
      value: 'To be confirmed',
    },
    {
      label: 'Payment terms',
      value: 'To be confirmed',
    },
    {
      label: 'Order window',
      value: 'To be confirmed',
    },
  ],
  next: ['Range review', 'Sample request'],
};
export const Gl = referenceImages.aubrac.kit;
export const Kl = [Gl[0], Gl[1], Gl[2]][0];
export const ql = [Gl[0], Gl[1], Gl[2]][1];
export const Jl = [Gl[0], Gl[1], Gl[2]][2];
export const U = {
  name: 'Maison Aubrac',
  product: 'Sculpted wool coat dress',
  ref: 'the sculpted wool coat dress',
  launch: 'March 12',
  colours: [
    {
      name: 'Umber black',
      hex: '#2E2520',
    },
  ],
  sizes: ['34', '36', '38', '40', '42'],
  idea: he.idea,
  line: he.line,
  checks: he.checks,
  formats: he.formats,
  campaign: referenceImages.aubrac.campaign,
  kit: {
    teaser: Kl ?? referenceImages.aubrac.campaign,
    launch: ql ?? referenceImages.aubrac.campaign,
    detail: Jl ?? referenceImages.aubrac.campaign,
  },
  image: referenceImages.aubrac.listing.ready
    ? referenceImages.aubrac.listing
    : (ql ?? referenceImages.aubrac.campaign),
};
export const Yl = he.steps.map((e, t) => ({
  ...e,
  asset: [U.kit.teaser, U.kit.launch, U.kit.detail][t] ?? U.kit.launch,
  headline: [he.idea, 'The coat dress.', 'Finished by hand.'][t] ?? he.idea,
  tone: t === 2 ? 'light' : 'dark',
  place: ['right', 'left', 'top'][t] ?? 'top',
}));
export const Xl = referenceImages.aubrac.pdp.map((e, t) => ({
  asset: e,
  label: ['Shape', 'Finish', 'Cloth'][t] ?? '',
  answers:
    [
      'One curved seam, shoulder to waist',
      'Hand-finished hem, covered buttons',
      'Umber-black, dense and matte',
    ][t] ?? '',
}));
export function MarketReveal({
  on: e,
  as: Component_t = 'div',
  pop: n = !1,
  delay: r = 0,
  className: i = '',
  style: a,
  children: o,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <Component_t
      data-on={e}
      className={`${n ? 'mk-pop' : 'mk-r'} ${i}`}
      style={{
        ...a,
        transitionDelay: e && r ? `${r}ms` : void 0,
      }}
    >
      {translateShowcaseNode(showcaseCopy, o)}
    </Component_t>
  );
}
export function Zl(e, t, n, r = 70) {
  if (t < n) return '';
  let i = e.split(' '),
    a = Math.min(i.length, Math.floor((t - n) / r) + 1);
  return i.slice(0, a).join(' ');
}
